import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Alert,
  TextField,
  TablePagination,
  InputAdornment,
  IconButton,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import {
  Search,
  Clear,
  Visibility,
  Settings,
  CloudUpload,
  ExpandMore,
  ExpandLess,
  Download,
  PlayArrow,
  Restaurant,
  Link,
  CardMembership,
  Business,
  Fingerprint,
  Build,
} from '@mui/icons-material';
import debounce from 'lodash.debounce';
import * as XLSX from 'xlsx';

// Styled table container with subtle shadow and animation
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  background: '#ffffff',
  overflowX: 'auto',
  transition: 'box-shadow 0.3s ease',
  '&:hover': {
    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
  },
}));

// Styled table with auto layout to fit content
const StyledTable = styled(Table)(({ theme }) => ({
  tableLayout: 'auto',
  width: '100%',
  borderCollapse: 'collapse',
  '& th, & td': {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

// Styled action button with consistent color
const ActionButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 500,
  borderRadius: 8,
  padding: theme.spacing(1, 2),
  marginRight: theme.spacing(1),
  backgroundColor: '#21ccbb',
  color: '#fff',
  '&:hover': {
    backgroundColor: '#1ab8a6',
  },
  '&.outlined': {
    backgroundColor: 'transparent',
    borderColor: '#21ccbb',
    color: '#21ccbb',
    '&:hover': {
      backgroundColor: 'rgba(33, 204, 187, 0.1)',
      borderColor: '#1ab8a6',
      color: '#1ab8a6',
    },
  },
  '&:disabled': {
    backgroundColor: '#cccccc',
    color: '#666666',
  },
  '& .MuiButton-startIcon': {
    marginRight: theme.spacing(0.5),
  },
  transition: 'transform 0.2s ease',
  '&:active': {
    transform: 'scale(0.95)',
  },
}));

// Styled search input with enhanced focus state
const SearchInput = styled(TextField)(({ theme }) => ({
  flex: 1,
  '& .MuiInputBase-root': {
    background: '#fff',
    borderRadius: 8,
    paddingRight: theme.spacing(1),
    transition: 'box-shadow 0.2s ease',
    '&:hover': {
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    },
    '&.Mui-focused': {
      boxShadow: '0 2px 12px rgba(33, 204, 187, 0.3)',
    },
  },
  '& .MuiInputBase-input': {
    padding: theme.spacing(1.5),
  },
}));

// Styled TableHead cell with adjusted styles
const StyledTableHeadCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  color: '#1e293b',
  padding: theme.spacing(1.5, 2),
  textAlign: 'center',
  whiteSpace: 'nowrap',
  backgroundColor: '#f4f6f8',
  '& > div': {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '& svg': {
    marginRight: theme.spacing(0.5),
    fontSize: 18,
    color: '#21ccbb',
  },
  minWidth: '100px',
  maxWidth: '200px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

// Styled TableBody cell with adjusted styles
const StyledTableBodyCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  textAlign: 'center',
  fontSize: '0.875rem',
  color: '#1e293b',
  '&:first-of-type': {
    textAlign: 'left',
  },
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  minWidth: '100px',
  maxWidth: '200px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

// Styled TableRow with hover effect
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: 'rgba(33, 204, 187, 0.1)',
    transition: 'background-color 0.2s',
  },
}));

// Styled Card for Excel Upload Section
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  background: 'linear-gradient(145deg, #ffffff, #f9f9f9)',
  marginTop: theme.spacing(3),
  padding: theme.spacing(3),
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 6px 24px rgba(0, 0, 0, 0.12)',
  },
}));

// Styled Upload Button
const UploadButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  padding: theme.spacing(1.5, 3),
  backgroundColor: '#21ccbb',
  color: '#fff',
  '&:hover': {
    backgroundColor: '#1ab8a6',
  },
  '&:disabled': {
    backgroundColor: '#cccccc',
    color: '#666666',
  },
  '& .MuiButton-startIcon': {
    marginRight: theme.spacing(1),
  },
  transition: 'transform 0.2s ease',
  '&:active': {
    transform: 'scale(0.95)',
  },
}));

// Styled Process Button
const ProcessButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  padding: theme.spacing(1.5, 3),
  backgroundColor: '#21ccbb',
  color: '#fff',
  '&:hover': {
    backgroundColor: '#1ab8a6',
  },
  '&:disabled': {
    backgroundColor: '#cccccc',
    color: '#666666',
  },
  '& .MuiButton-startIcon': {
    marginRight: theme.spacing(1),
  },
  transition: 'transform 0.2s ease',
  '&:active': {
    transform: 'scale(0.95)',
  },
}));

// Styled Toggle Button
const ToggleButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  padding: theme.spacing(1.5, 3),
  backgroundColor: '#21ccbb', // Corrected from background:Bcolor
  color: '#fff',
  textTransform: 'none',
  fontWeight: 500,
  marginLeft: theme.spacing(2),
  '&:hover': {
    backgroundColor: '#1ab8a6',
  },
  '&:disabled': {
    backgroundColor: '#cccccc',
    color: '#666666',
  },
  '& .MuiButton-startIcon': {
    marginRight: theme.spacing(1),
  },
  transition: 'transform 0.2s ease',
  '&:active': {
    transform: 'scale(0.95)',
  },
}));

// Styled Download Button
const DownloadButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  padding: theme.spacing(1.5, 3),
  backgroundColor: '#21ccbb',
  color: '#fff',
  textTransform: 'none',
  fontWeight: 500,
  marginLeft: theme.spacing(2),
  '&:hover': {
    backgroundColor: '#1ab8a6',
  },
  '&:disabled': {
    backgroundColor: '#cccccc',
    color: '#666666',
  },
  '& .MuiButton-startIcon': {
    marginRight: theme.spacing(1),
  },
  transition: 'transform 0.2s ease',
  '&:active': {
    transform: 'scale(0.95)',
  },
}));

const Invoice = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [excelFile, setExcelFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState([]);
  const [processingError, setProcessingError] = useState(null);
  const [showBulkProcess, setShowBulkProcess] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [setFactureLoading, setSetFactureLoading] = useState({});
  const bulkProcessRef = useRef(null);
  const toggleButtonRef = useRef(null);

  const debouncedFetchRestaurants = useCallback(
    debounce(async (query, pageNum, limit) => {
      try {
        setSearchLoading(true);
        setLoading(true);
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found');
        }
        const response = await axios.get('https://apibackoffice.lalivraison.ma/api/restaurants/eligible-for-invoicing', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            search: query,
            page: pageNum + 1,
            limit,
          },
        });
        setRestaurants(response.data.restaurants);
        setTotalCount(response.data.pagination.total);
        setError(null);
      } catch (err) {
        setError(err.message || 'Erreur lors de la récupération des restaurants');
        console.error('Error fetching restaurants:', err);
      } finally {
        setSearchLoading(false);
        setLoading(false);
      }
    }, 500),
    []
  );

  useEffect(() => {
    debouncedFetchRestaurants(searchQuery, page, rowsPerPage);
    return () => debouncedFetchRestaurants.cancel();
  }, [searchQuery, page, rowsPerPage, debouncedFetchRestaurants]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSetFacture = async (ice) => {
    setSetFactureLoading((prev) => ({ ...prev, [ice]: true }));
    try {
      await axios.post(
        `https://lalivraisonv2-api.lalivraison.ma/factures/facture?restaurant_id=${ice}`,
        {}
      );
      alert(`Facture set successfully for ICE: ${ice}`);
    } catch (err) {
      alert(`Error setting facture for ICE: ${ice}. ${err.message}`);
      console.error('Error setting facture:', err);
    } finally {
      setSetFactureLoading((prev) => ({ ...prev, [ice]: false }));
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    if (!validTypes.includes(file.type)) {
      setProcessingError('Please upload a valid Excel file (.xlsx)');
      return;
    }

    setExcelFile(file);
    setProcessingError(null);
    setProcessingStatus([]);
  };

  const processExcelFile = async () => {
    if (!excelFile) {
      setProcessingError('No file selected. Please upload an Excel file.');
      return;
    }

    setProcessing(true);
    setProcessingError(null);
    setProcessingStatus([]);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        const requiredHeaders = [
          'restaurant_id',
          'url_unique',
          'nom_societe',
          'ice',
          'regularisation',
          'solde_dispatch',
          'solde_request_recharge',
          'solde_facture',
        ];
        const firstRow = jsonData[0];
        const hasRequiredHeaders = requiredHeaders.every((header) => header in firstRow);
        if (!hasRequiredHeaders) {
          setProcessingError('Excel file is missing required headers.');
          setProcessing(false);
          return;
        }

        for (let i = 0; i < jsonData.length; i++) {
          const row = jsonData[i];
          const ice = row.ice;

          if (!ice || typeof ice !== 'string' || ice.trim() === '') {
            setProcessingStatus((prev) => [
              ...prev,
              `Row ${i + 1}: SKIPPED - Invalid or missing ICE.`,
            ]);
            continue;
          }

          setProcessingStatus((prev) => [
            ...prev,
            `Row ${i + 1}: Processing ICE ${ice}...`,
          ]);

          try {
            await axios.post(
              `https://lalivraisonv2-api.lalivraison.ma/factures/facture?restaurant_id=${ice}`,
              {}
            );
            setProcessingStatus((prev) => [
              ...prev,
              `Row ${i + 1}: Successfully processed ICE ${ice}.`,
            ]);
          } catch (err) {
            const errorMessage = `Row ${i + 1}: Failed to process ICE ${ice}. Error: ${err.message}`;
            setProcessingStatus((prev) => [...prev, errorMessage]);
            console.error(errorMessage, err);
          }

          if (i < jsonData.length - 1) {
            setProcessingStatus((prev) => [
              ...prev,
              `Waiting 30 seconds before processing the next row...`,
            ]);
            await new Promise((resolve) => setTimeout(resolve, 30000));
          }
        }

        setProcessingStatus((prev) => [
          ...prev,
          'All rows processed. Check the status for details.',
        ]);
      };

      reader.onerror = (err) => {
        setProcessingError('Error reading the Excel file.');
        console.error('Error reading Excel file:', err);
        setProcessing(false);
      };

      reader.readAsArrayBuffer(excelFile);
    } catch (err) {
      setProcessingError(`Error processing Excel file: ${err.message}`);
      console.error('Error processing Excel file:', err);
      setProcessing(false);
    } finally {
      setProcessing(false);
      setExcelFile(null);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      setDownloadLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.get('https://apibackoffice.lalivraison.ma/api/restaurants/eligible-for-invoicing', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          search: searchQuery,
          all: 'true',
        },
      });

      const allRestaurants = response.data.restaurants;

      const exportData = allRestaurants.map((restaurant) => ({
        restaurant_id: restaurant.restaurant_id,
        url_unique: restaurant.url_unique || '',
        nom_societe: restaurant.nom_societe || '',
        ice: restaurant.ice || '',
        regularisation: restaurant.regularisation || 0,
        solde_dispatch: restaurant.solde_dispatch || 0,
        solde_request_recharge: restaurant.solde_request_recharge || 0,
        solde_facture: restaurant.solde_facture || 0,
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Restaurants');

      XLSX.writeFile(workbook, `eligible_restaurants_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      setError(err.message || 'Error downloading restaurants');
      console.error('Error downloading restaurants:', err);
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleToggleBulkProcess = () => {
    setShowBulkProcess((prev) => !prev);
  };

  useEffect(() => {
    if (showBulkProcess && bulkProcessRef.current) {
      bulkProcessRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    } else if (!showBulkProcess && toggleButtonRef.current) {
      toggleButtonRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [showBulkProcess]);

  if (loading && !searchLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px', flexDirection: 'column', gap: 2 }}>
        <CircularProgress size={40} sx={{ color: '#21ccbb' }} />
        <Typography variant="body2" color="textSecondary">Chargement des restaurants...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, fontFamily: "'Inter', sans-serif" }}>
      <Typography
        variant="h5"
        gutterBottom
        sx={{ fontWeight: 600, color: '#1e293b', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <Restaurant sx={{ color: '#21ccbb' }} />
        Restaurants Éligibles pour Facturation
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <SearchInput
          variant="outlined"
          placeholder="Rechercher par nom, ICE, société..."
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: '#21ccbb' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {searchLoading ? (
                  <CircularProgress size={20} sx={{ color: '#21ccbb' }} />
                ) : (
                  searchQuery && (
                    <IconButton onClick={handleClearSearch} size="small">
                      <Clear sx={{ color: '#21ccbb' }} />
                    </IconButton>
                  )
                )}
              </InputAdornment>
            ),
          }}
        />
        <ToggleButton
          ref={toggleButtonRef}
          variant="contained"
          onClick={handleToggleBulkProcess}
          startIcon={showBulkProcess ? <ExpandLess /> : <ExpandMore />}
        >
          {showBulkProcess ? 'Masquer le traitement en masse' : 'Afficher le traitement en masse'}
        </ToggleButton>
        <DownloadButton
          variant="contained"
          onClick={handleDownloadExcel}
          startIcon={<Download />}
          disabled={totalCount === 0 || downloadLoading}
        >
          {downloadLoading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Télécharger Excel'}
        </DownloadButton>
      </Box>
      {restaurants.length === 0 ? (
        <Typography color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
          Aucun restaurant éligible pour facturation.
        </Typography>
      ) : (
        <>
          <StyledTableContainer component={Paper}>
            <StyledTable>
              <TableHead>
                <TableRow>
                  <StyledTableHeadCell>
                    <div><Restaurant /> Désignation</div>
                  </StyledTableHeadCell>
                  <StyledTableHeadCell>
                    <div><Link /> URL Unique</div>
                  </StyledTableHeadCell>
                  <StyledTableHeadCell>
                    <div><CardMembership /> Type Abonnement</div>
                  </StyledTableHeadCell>
                  <StyledTableHeadCell>
                    <div><Business /> Nom Société</div>
                  </StyledTableHeadCell>
                  <StyledTableHeadCell>
                    <div><Fingerprint /> ICE</div>
                  </StyledTableHeadCell>
                  <StyledTableHeadCell>
                    <div><Build /> Actions</div>
                  </StyledTableHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {restaurants.map((restaurant) => (
                  <StyledTableRow key={restaurant.ice}>
                    <StyledTableBodyCell>{restaurant.designation}</StyledTableBodyCell>
                    <StyledTableBodyCell>{restaurant.url_unique || 'N/A'}</StyledTableBodyCell>
                    <StyledTableBodyCell>
                      {restaurant.type_abonnement === 1 ? 'Sans livreur' : restaurant.type_abonnement === 2 ? 'Avec livreur' : restaurant.type_abonnement === 0 ? 'Sans livreur' : 'N/A'}
                    </StyledTableBodyCell>
                    <StyledTableBodyCell>{restaurant.nom_societe || 'N/A'}</StyledTableBodyCell>
                    <StyledTableBodyCell>{restaurant.ice || 'N/A'}</StyledTableBodyCell>
                    <StyledTableBodyCell>
                      <ActionButton
                        variant="contained"
                        size="small"
                        startIcon={<Visibility />}
                        href={`https://channelmanager.ma/facture/invoice.php?restaurant_id=${restaurant.ice}`}
                        target="_blank"
                      >
                        Afficher
                      </ActionButton>
                      <ActionButton
                        variant="outlined"
                        size="small"
                        startIcon={<Settings />}
                        onClick={() => handleSetFacture(restaurant.ice)}
                        disabled={setFactureLoading[restaurant.ice]}
                        className="outlined"
                      >
                        {setFactureLoading[restaurant.ice] ? (
                          <CircularProgress size={20} sx={{ color: '#21ccbb' }} />
                        ) : (
                          'Définir'
                        )}
                      </ActionButton>
                    </StyledTableBodyCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </StyledTable>
          </StyledTableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Lignes par page :"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count}`}
            sx={{ mt: 2 }}
          />
        </>
      )}

      {showBulkProcess && (
        <StyledCard ref={bulkProcessRef}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <CloudUpload sx={{ fontSize: 40, color: '#21ccbb' }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  Traitement en Masse des Factures
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  Téléchargez un fichier Excel (.xlsx) pour traiter les factures de plusieurs restaurants.
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <UploadButton
                variant="contained"
                component="label"
                startIcon={<CloudUpload />}
              >
                Sélectionner le Fichier Excel
                <input
                  type="file"
                  accept=".xlsx, .xls"
                  hidden
                  onChange={handleFileUpload}
                />
              </UploadButton>
              {excelFile && (
                <Typography variant="body2" sx={{ color: '#1e293b', fontStyle: 'italic' }}>
                  {excelFile.name}
                </Typography>
              )}
              <ProcessButton
                variant="contained"
                onClick={processExcelFile}
                disabled={!excelFile || processing}
                startIcon={processing ? null : <PlayArrow />}
              >
                {processing ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Traiter les Factures'}
              </ProcessButton>
            </Box>
            {processingError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {processingError}
              </Alert>
            )}
            {processingStatus.length > 0 && (
              <Box
                sx={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  p: 2,
                  border: '1px solid #ddd',
                  borderRadius: 2,
                  backgroundColor: '#f9f9f9',
                }}
              >
                {processingStatus.map((status, index) => (
                  <Typography
                    key={index}
                    variant="body2"
                    sx={{
                      color: status.includes('Failed') ? 'red' : status.includes('Successfully') ? 'green' : '#1e293b',
                      mb: 0.5,
                    }}
                  >
                    {status}
                  </Typography>
                ))}
              </Box>
            )}
          </CardContent>
        </StyledCard>
      )}
    </Box>
  );
};

export default Invoice;